import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

// ─── Theme Modes & Metadata ───────────────────────────────────────────────────

export const THEME_MODES = [
  { id: 'system', label: 'System', icon: 'monitor' },
  { id: 'dark',   label: 'Dark',   icon: 'moon' },
  { id: 'light',  label: 'Light',  icon: 'sun' },
];

export const THEMES = [
  { name: 'dark',   color: '#0B1120', accent: '#6366F1', label: 'Dark Mode (Linear / Indigo)' },
  { name: 'light',  color: '#F8FAFC', accent: '#4F46E5', label: 'Light Mode (Modern SaaS)' },
  { name: 'system', color: '#111827', accent: '#6366F1', label: 'System Preference' },
];

const STORAGE_KEY = 'vfl-theme-mode';
const DEFAULT_MODE = 'system';

// ─── Helper to query system dark mode ──────────────────────────────────────────

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// ─── Context ──────────────────────────────────────────────────────────────────

export const ThemeContext = createContext({
  mode: DEFAULT_MODE,
  theme: 'dark',
  isDark: true,
  setMode: () => {},
  setTheme: () => {},
  toggleTheme: () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'dark' || saved === 'light' || saved === 'system') {
        return saved;
      }
      return DEFAULT_MODE;
    } catch {
      return DEFAULT_MODE;
    }
  });

  const [systemIsDark, setSystemIsDark] = useState(getSystemPrefersDark);

  // Listen to OS system color-scheme changes when in 'system' mode
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handler = (e) => {
      setSystemIsDark(e.matches);
    };

    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else if (mq.addListener) {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
  }, []);

  // Compute resolved theme ('dark' or 'light')
  const resolvedIsDark = useMemo(() => {
    if (mode === 'dark') return true;
    if (mode === 'light') return false;
    return systemIsDark;
  }, [mode, systemIsDark]);

  const resolvedTheme = resolvedIsDark ? 'dark' : 'light';

  // Apply [data-theme] and .dark class to <html>
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    if (resolvedIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    try {
      localStorage.setItem(STORAGE_KEY, mode);
      localStorage.setItem('app-color-theme', resolvedTheme);
      localStorage.setItem('vfl_woms_theme', resolvedTheme);
    } catch {}
  }, [mode, resolvedTheme, resolvedIsDark]);

  const setMode = useCallback((newMode) => {
    if (newMode === 'dark' || newMode === 'light' || newMode === 'system') {
      setModeState(newMode);
    }
  }, []);

  // Alias setTheme for backwards compatibility
  const setTheme = useCallback((newTheme) => {
    if (newTheme === 'dark' || newTheme === 'light' || newTheme === 'system') {
      setMode(newTheme);
    } else if (newTheme === 'emerald' || newTheme === 'sky' || newTheme === 'amber') {
      // Legacy theme names mapped to light mode
      setMode('light');
    }
  }, [setMode]);

  // Toggle cycles through modes: dark -> light -> system -> dark
  const toggleTheme = useCallback(() => {
    setModeState((current) => {
      if (current === 'system') return resolvedIsDark ? 'light' : 'dark';
      return current === 'dark' ? 'light' : 'dark';
    });
  }, [resolvedIsDark]);

  const contextValue = useMemo(() => ({
    mode,
    theme: resolvedTheme,
    isDark: resolvedIsDark,
    setMode,
    setTheme,
    toggleTheme,
  }), [mode, resolvedTheme, resolvedIsDark, setMode, setTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTheme() {
  return useContext(ThemeContext);
}
